import { MessageCircle, Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Botão flutuante de WhatsApp.
 * Regras:
 * - Visível apenas para usuários autenticados (login obrigatório).
 * - Só renderiza o link real quando /config/whatsapp responde com um número válido.
 *   Isso evita o flash com o placeholder 5511999999999 quando a env var
 *   INSTITUTE_WHATSAPP_NUMBER não está definida.
 * - Usuários não logados veem um botão discreto que leva para a tela de login.
 */
export default function WhatsAppFloat() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [link, setLink] = useState(null);

  useEffect(() => {
    if (!user) return; // só carrega o número quando estiver logado
    api
      .get("/config/whatsapp")
      .then(({ data }) => {
        const number = String(data?.number || "").replace(/\D/g, "");
        // Placeholder inválido (todos 9s) é ignorado
        if (number && number.length >= 10 && number !== "5511999999999") {
          const text = encodeURIComponent("Olá! Vim pelo site do Instituto Cuidar e gostaria de conversar.");
          setLink(`https://wa.me/${number}?text=${text}`);
        } else {
          setLink(null);
        }
      })
      .catch(() => setLink(null));
  }, [user]);

  // Enquanto o AuthContext está resolvendo, não mostra nada
  if (loading) return null;

  // Usuário NÃO logado: botão de "Entrar para falar no WhatsApp"
  if (!user) {
    return (
      <button
        onClick={() => navigate("/entrar")}
        data-testid="whatsapp-float-login-btn"
        aria-label="Faça login para falar no WhatsApp"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-3.5 text-white shadow-[0_10px_30px_rgba(37,211,102,0.35)] hover:scale-[1.03] transition-transform"
      >
        <Lock className="h-4 w-4" />
        <span className="hidden sm:inline font-medium text-sm">Entrar para falar no WhatsApp</span>
        <span className="sm:hidden font-medium text-sm">Entrar</span>
      </button>
    );
  }

  // Logado, mas ainda não temos o link (config carregando ou número inválido no servidor):
  // não mostra o botão pra evitar mandar a família pro número errado.
  if (!link) return null;

  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-float-btn"
      aria-label="Falar no WhatsApp"
      className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-3.5 text-white shadow-[0_10px_30px_rgba(37,211,102,0.35)] hover:scale-[1.03] transition-transform"
    >
      <MessageCircle className="h-5 w-5" />
      <span className="hidden sm:inline font-medium text-sm">Falar no WhatsApp</span>
    </a>
  );
}
